"use client";
import { useEffect, useMemo, useState } from "react";
import {
  Room,
  RoomEvent,
  RemoteTrack,
  isVideoTrack,
  VideoQuality,
  LocalVideoTrack,
  ParticipantEvent,
  RemoteTrackPublication
} from "livekit-client";

export interface LowCPUOptimizerOptions {
  reducePublisherVideoQuality: boolean;
  reduceSubscriberVideoQuality: boolean;
  disableVideoProcessing: boolean;
}

const DEFAULT_OPTIONS: LowCPUOptimizerOptions = {
  reducePublisherVideoQuality: true,
  reduceSubscriberVideoQuality: true,
  disableVideoProcessing: false
};

/** Reduces video quality to LOW for all remote participants. */
const reduceRemoteVideoQuality = (room: Room): void => {
  room.remoteParticipants.forEach((participant) => {
    participant.videoTrackPublications.forEach((pub) => {
      pub.setVideoQuality(VideoQuality.LOW);
    });
  });
};

/**
 * Optimizes performance for low-CPU devices.
 * Reduces video quality or disables processing when CPU constraints are detected.
 */
export const useLowCPUOptimizer = (room: Room, options: Partial<LowCPUOptimizerOptions> = {}): boolean => {
  const [lowPowerMode, setLowPowerMode] = useState(false);
  const opts = useMemo(() => ({ ...DEFAULT_OPTIONS, ...options }), [options]);

  // Handle CPU constraint event on local track
  useEffect(() => {
    const handleCpuConstrained = (track: LocalVideoTrack) => {
      setLowPowerMode(true);
      console.warn("[Performance] CPU constrained, enabling low power mode");

      if (opts.reducePublisherVideoQuality) track.prioritizePerformance();
      if (opts.disableVideoProcessing && isVideoTrack(track)) track.stopProcessor();
      if (opts.reduceSubscriberVideoQuality) reduceRemoteVideoQuality(room);
    };

    room.localParticipant.on(ParticipantEvent.LocalTrackCpuConstrained, handleCpuConstrained);
    return () => {
      room.localParticipant.off(ParticipantEvent.LocalTrackCpuConstrained, handleCpuConstrained);
    };
  }, [room, opts]);

  // Auto-reduce quality for newly subscribed tracks in low power mode
  useEffect(() => {
    if (!lowPowerMode || !opts.reduceSubscriberVideoQuality) return;

    const lowerQuality = (_: RemoteTrack, publication: RemoteTrackPublication) => {
      publication.setVideoQuality(VideoQuality.LOW);
    };

    room.on(RoomEvent.TrackSubscribed, lowerQuality);
    return () => {
      room.off(RoomEvent.TrackSubscribed, lowerQuality);
    };
  }, [lowPowerMode, room, opts.reduceSubscriberVideoQuality]);

  return lowPowerMode;
};
